
import React, { useState, useMemo } from 'react';
import { useAppContext } from '../contexts/AppContext';
import Card from '../components/ui/Card';
import { Shield, AlertTriangle, CheckCircle, Activity, Layers } from 'lucide-react';
import ActiveProjectBadge from '../components/ActiveProjectBadge';

// Mock Data for demonstration until backend is fully ready
const MOCK_SAFETY_GOALS = [
    { id: 'SG-01', description: 'Prevent unintended acceleration > 0.3g', asil: 'D', status: 'Compliant' },
    { id: 'SG-02', description: 'Ensure braking torque availability', asil: 'D', status: 'Compliant' },
    { id: 'SG-03', description: 'Notify driver of steering loss within 500ms', asil: 'B', status: 'Review' },
];

const MOCK_HARDWARE_METRICS = {
    spfm: 99.4, // Single Point Fault Metric
    lfm: 94.2,  // Latent Fault Metric
    pmhf: 4.5,  // Probabilitic Metric for Random Hardware Failures (FIT)
    asilTarget: 'D'
};

const FunctionalSafetyDashboard: React.FC = () => {
    const { allData, activeProject } = useAppContext();
    const [activeTab, setActiveTab] = useState<'overview' | 'hara' | 'goals' | 'hardware'>('overview');

    // Filter relevant data for HARA (Hazard Analysis)
    // In a real scenario, this would filter FMEA items marked as 'hazards'
    const hazardItems = useMemo(() => {
        if (!allData.fmeaItems) return [];
        // Just for demo, assuming high severity items are candidates for hazards
        return allData.fmeaItems.filter(item => item.S >= 9 && (!activeProject || item.projectName === activeProject || activeProject.includes(item.projectName)));
    }, [allData.fmeaItems, activeProject]);

    const tabs = [
        { id: 'overview', label: 'Overview', icon: Activity },
        { id: 'hara', label: 'HARA', icon: AlertTriangle },
        { id: 'goals', label: 'Safety Goals', icon: Shield },
        { id: 'hardware', label: 'Hardware Metrics', icon: Layers },
    ];

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold text-text-primary">Functional Safety Dashboard</h1>
                    <div className="flex items-center gap-2 mt-1">
                        <p className="text-text-secondary text-sm">ISO 26262 / IEC 61508 Compliance Hub</p>
                        <ActiveProjectBadge />
                    </div>
                </div>
            </div>

            {/* Navigation Tabs */}
            <div className="flex space-x-1 bg-card border border-border rounded-lg p-1 w-fit">
                {tabs.map(tab => {
                    const Icon = tab.icon;
                    return (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id as any)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === tab.id
                                ? 'bg-primary text-white shadow-sm'
                                : 'text-text-secondary hover:bg-background hover:text-text-primary'
                                }`}
                        >
                            <Icon size={16} />
                            {tab.label}
                        </button>
                    )
                })}
            </div>

            {/* Content Area */}
            {activeTab === 'overview' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <Card>
                        <div className="flex flex-col items-center justify-center py-4">
                            <Shield size={40} className="text-emerald-500 mb-3" />
                            <h3 className="text-2xl font-bold text-text-primary">ASIL D</h3>
                            <p className="text-sm text-text-secondary">Highest Safety Integrity</p>
                        </div>
                    </Card>
                    <Card>
                        <div className="flex flex-col items-center justify-center py-4">
                            <div className="flex items-baseline">
                                <h3 className="text-2xl font-bold text-text-primary">{MOCK_HARDWARE_METRICS.spfm}</h3>
                                <span className="text-sm text-text-secondary">%</span>
                            </div>
                            <p className="text-sm text-text-secondary mt-2">SPFM (Target {'>'} 99%)</p>
                            <div className="w-full bg-slate-200 dark:bg-slate-700 h-1.5 mt-3 rounded-full overflow-hidden">
                                <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${MOCK_HARDWARE_METRICS.spfm}%` }} />
                            </div>
                        </div>
                    </Card>
                    <Card>
                        <div className="flex flex-col items-center justify-center py-4">
                            <div className="flex items-baseline">
                                <h3 className="text-2xl font-bold text-text-primary">{MOCK_HARDWARE_METRICS.lfm}</h3>
                                <span className="text-sm text-text-secondary">%</span>
                            </div>
                            <p className="text-sm text-text-secondary mt-2">LFM (Target {'>'} 90%)</p>
                            <div className="w-full bg-slate-200 dark:bg-slate-700 h-1.5 mt-3 rounded-full overflow-hidden">
                                <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${MOCK_HARDWARE_METRICS.lfm}%` }} />
                            </div>
                        </div>
                    </Card>
                    <Card>
                        <div className="flex flex-col items-center justify-center py-4">
                            <AlertTriangle size={40} className="text-amber-500 mb-3" />
                            <h3 className="text-2xl font-bold text-text-primary">{hazardItems.length}</h3>
                            <p className="text-sm text-text-secondary">Potential Hazards (S ≥ 9)</p>
                        </div>
                    </Card>

                    <div className="lg:col-span-2">
                        <Card title="Safety Goals Status">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm text-left">
                                    <thead className="text-xs text-text-secondary uppercase bg-background/50 border-b border-border">
                                        <tr>
                                            <th className="px-4 py-3">ID</th>
                                            <th className="px-4 py-3">Safety Goal</th>
                                            <th className="px-4 py-3">ASIL</th>
                                            <th className="px-4 py-3 text-right">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {MOCK_SAFETY_GOALS.map(goal => (
                                            <tr key={goal.id} className="border-b border-border/50 last:border-0 hover:bg-background/30 cursor-pointer">
                                                <td className="px-4 py-3 font-medium text-primary">{goal.id}</td>
                                                <td className="px-4 py-3">{goal.description}</td>
                                                <td className="px-4 py-3">
                                                    <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-bold ${goal.asil === 'D' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                                                        goal.asil === 'C' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' :
                                                            goal.asil === 'B' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
                                                                'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300'
                                                        }`}>
                                                        {goal.asil}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    <span className={`inline-flex items-center gap-1.5 ${goal.status === 'Compliant' ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'
                                                        }`}>
                                                        {goal.status === 'Compliant' ? <CheckCircle size={14} /> : <Activity size={14} />}
                                                        {goal.status}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    </div>
                    <div className="lg:col-span-2">
                        <Card title="System Decomposition">
                            <div className="flex items-center justify-center h-48 text-text-muted italic border-2 border-dashed border-border rounded-lg bg-background/30">
                                System Architecture Visualization Placeholder
                            </div>
                        </Card>
                    </div>
                </div>
            )}

            {activeTab === 'hara' && (
                <Card title="Hazard Analysis & Risk Assessment (HARA)">
                    <div className="space-y-4">
                        <p className="text-text-secondary text-sm">
                            High severity failure modes that may represent hazardous events.
                            <br />
                            <span className="text-xs italic opacity-75">Filtered for Severity ≥ 9</span>
                        </p>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left">
                                <thead className="text-xs text-text-secondary uppercase bg-background/50 border-b border-border">
                                    <tr>
                                        <th className="px-4 py-3">FMEA ID</th>
                                        <th className="px-4 py-3">Function</th>
                                        <th className="px-4 py-3">Failure Mode (Hazard)</th>
                                        <th className="px-4 py-3">Effect</th>
                                        <th className="px-4 py-3">Severity</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {hazardItems.length > 0 ? hazardItems.map(item => (
                                        <tr key={item.id} className="border-b border-border/50 last:border-0 hover:bg-background/30">
                                            <td className="px-4 py-3 font-mono text-xs">{item.id}</td>
                                            <td className="px-4 py-3">{item.function}</td>
                                            <td className="px-4 py-3 font-medium text-text-primary">{item.failureMode}</td>
                                            <td className="px-4 py-3 text-text-secondary">{item.effects}</td>
                                            <td className="px-4 py-3">
                                                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 font-bold text-xs ring-1 ring-red-500/20">
                                                    {item.S}
                                                </span>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan={5} className="px-4 py-8 text-center text-text-muted">
                                                No high-severity hazards (S ≥ 9) found in current project.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </Card>
            )}
            {/* Placeholders for other tabs */}
            {(activeTab === 'goals' || activeTab === 'hardware') && (
                <Card>
                    <div className="flex flex-col items-center justify-center py-12 text-center text-text-muted">
                        <Layers size={48} className="mb-4 opacity-50" />
                        <h3 className="text-lg font-medium">Detailed View Coming Soon</h3>
                        <p className="max-w-md mt-2">This section is currently under development to support full ISO 26262 Part 5 integration.</p>
                    </div>
                </Card>
            )}
        </div>
    );
};

export default FunctionalSafetyDashboard;
